import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { Send, Reply, WifiOff, Wifi, Refresh } from "@mui/icons-material";
import useNotification from "../hooks/useNotification";
import { useSignalRContext } from "../contextProviders/SignalRProvider";
import { useMessagesApi } from "../hooks/useMessagesApi";
import { formatLocalDate, formatLocalTime } from "../utils/formatDateTime";
import api from "../API/axios";

// Utility: Group messages by date and insert date stripes
function groupMessagesWithDateStripes(messages) {
  if (!Array.isArray(messages) || messages.length === 0) return [];
  // Sort messages by timestamp ascending
  const sorted = [...messages].sort(
    (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
  );
  const result = [];
  let lastDate = null;
  sorted.forEach((msg) => {
    const msgDate = new Date(msg.timestamp);
    // Format as yyyy-MM-dd for comparison
    const dateKey =
      msgDate.getFullYear() +
      "-" +
      String(msgDate.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(msgDate.getDate()).padStart(2, "0");
    if (dateKey !== lastDate) {
      result.push({ type: "date-stripe", date: msgDate });
      lastDate = dateKey;
    }
    result.push({ type: "message", ...msg });
  });
  return result;
}

const ChatInterface = ({
  conversationId,
  consumer,
  allMessages, // Optional: initial messages, ChatInterface can also fetch independently
  initialPaginationInfo, // Optional: { totalCount, hasMore } from parent's paginated fetch
  currentOperatorId, // ID of current operator viewing this conversation
  onOperatorMessageSent, // Callback when operator sends a message
  onInboundMessageReceived, // Callback when inbound message is received (for timer reset)
}) => {
  // Request browser notification permission on mount
  useEffect(() => {
    if (
      window.Notification &&
      Notification.permission !== "granted" &&
      Notification.permission !== "denied"
    ) {
      Notification.requestPermission();
    }
  }, []);
  // ...existing code...
  const [messages, setMessages] = useState([]);
  const [replyText, setReplyText] = useState("");
  const [consumerCredits, setConsumerCredits] = useState(0);
  const [sending, setSending] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(0);
  // Pagination state
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const [totalMessageCount, setTotalMessageCount] = useState(0);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [currentSkip, setCurrentSkip] = useState(0);
  const PAGE_SIZE = 20;
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const { showNotification } = useNotification();

  // Admin portal - always operator role
  const userInfo = JSON.parse(localStorage.getItem("user") || "{}");
  const userRole = "operator"; // Admin portal = always operator
  const userId = userInfo.id;
  const accessToken = localStorage.getItem("token");

  // SignalR messaging hook for real-time functionality
  // Use SignalR context for real-time features
  const signalR = useSignalRContext();
  // Join SignalR conversation group for real-time updates
  useEffect(() => {
    if (signalR.connection && signalR.isConnected && conversationId) {
      signalR.connection
        .invoke("JoinConversation", conversationId)
        .then(() => {
          // Track conversation for reconnection recovery
          if (signalR.connection.trackConversation) {
            signalR.connection.trackConversation(conversationId);
          }
        })
        .catch((error) => {
          console.error(`Failed to join conversation ${conversationId}:`, error);
        });
    }

    // Leave group on unmount or conversation change
    return () => {
      if (signalR.connection && conversationId) {
        // Untrack conversation from reconnection recovery
        if (signalR.connection.untrackConversation) {
          signalR.connection.untrackConversation(conversationId);
        }
        if (signalR.isConnected) {
          signalR.connection
            .invoke("LeaveConversation", conversationId)
            .catch(console.error);
        }
      }
    };
  }, [signalR.connection, signalR.isConnected, conversationId, userRole]);

  // Messages API for operator messaging in admin portal
  const {
    sendOperatorMessage,
    markAllMessagesAsDelivered,
    getConversationMessages,
    getPaginatedMessages,
  } = useMessagesApi();

  // Function to fetch consumer credit balance by ID
  useEffect(() => {
    fetchConsumerCredits(consumer?.id);
  }, [consumer?.id]);

  const fetchConsumerCreditsById = useCallback(
    async (consumerId) => {
      if (!consumerId) {
        console.warn("⚠️ No consumer ID provided to fetch credits");
        return;
      }

      try {
        // Add cache-busting headers to ensure fresh credit data
        const response = await api.get(
          `/consumer/${consumerId}/credits/balance`,
          {
            headers: {
              "Cache-Control": "no-cache, no-store, must-revalidate",
              Pragma: "no-cache",
              Expires: "0",
            },
            // Add timestamp to URL to prevent browser caching
            params: {
              _t: Date.now(),
            },
          }
        );

        // With axios, response.data contains the parsed JSON automatically
        const result = response.data;

        // Handle the API response format: {"success":true,"message":"Success","data":26669,"errors":[]}
        let balance = 0;
        if (typeof result === "number") {
          balance = result;
        } else if (result && typeof result.data === "number") {
          balance = result.data;
        } else if (result && typeof result.data === "string") {
          balance = parseInt(result.data, 10);
        } else if (
          result &&
          result.success &&
          typeof result.data === "number"
        ) {
          // Handle the specific API format you're seeing
          balance = result.data;
        }

        setConsumerCredits(balance);
      } catch (error) {
        console.error("❌ Error fetching consumer credits:", error);
        setConsumerCredits(0);
      }
    },
    [accessToken]
  );

  // Function to fetch consumer credit balance
  const fetchConsumerCredits = useCallback(async () => {
    if (!consumer?.id) {
      console.warn("⚠️ No consumer ID available to fetch credits");
      return;
    }

    await fetchConsumerCreditsById(consumer.id);
  }, [consumer?.id, fetchConsumerCreditsById]);

  // Handle new messages received via SignalR - wrapped in useCallback to prevent stale closures
  const handleNewMessage = useCallback((message) => {
    // Add the new message to the local state
    setMessages((prev) => {
      // Check if message already exists to prevent duplicates
      const exists = prev.find((m) => m.id === message.id);
      if (exists) {
        return prev;
      }

      // Format message inline to avoid dependency issues
      const formattedMessage = {
        id: message.id,
        text: message.body || message.text,
        direction: message.direction === "Outbound" ? "outbound" : "inbound",
        timestamp: message.createdAt || message.CreatedAt || message.timestamp,
        sender: message.operatorName || consumer?.name || "Consumer",
        operatorId: message.operatorId,
        consumerId: message.consumerId,
      };

      // Show notification for inbound messages only if not already in this conversation
      if (formattedMessage.direction === "inbound") {
        // Notify parent component (MessageReply) to reset timer
        if (onInboundMessageReceived) {
          onInboundMessageReceived();
        }
        
        // Only show browser notification if window is not visible (no snackbar)
        if (document.visibilityState !== "visible") {
          // Browser tab notification only (no snackbar)
          if (window.Notification && Notification.permission === "granted") {
            new Notification("New Chat Message", {
              body: `${formattedMessage.sender}: ${
                formattedMessage.text?.substring(0, 40) || "..."
              }`,
              icon: "/favicon.ico",
            });
          }
        }
      }

      return [...prev, formattedMessage];
    });

    // Auto-scroll to new message
    setTimeout(scrollToBottom, 100);

    // Update consumer credits if it's an outbound message
    if (message.direction === "Outbound" && consumer?.id) {
      setConsumerCredits((prev) => Math.max(0, prev - 1));
    }
  }, [consumer?.name, consumer?.id, onInboundMessageReceived, showNotification]);

  // Determine if message should be on right side (sent by operator)
  const isMyMessage = (message) => {
    // In admin portal: outbound = operator sent, inbound = consumer sent
    return message.direction === "outbound" || message.operatorId === userId;
  };

  // Fetch messages from API with rate limiting (using pagination)
  const fetchMessages = useCallback(async () => {
    if (!conversationId) return;

    // Rate limiting: prevent too frequent calls
    const now = Date.now();
    if (now - lastRefreshTime < 500) {
      return;
    }

    setLoadingMessages(true);
    setLastRefreshTime(now);

    try {
      // Use paginated API - fetch last 20 messages initially
      const result = await getPaginatedMessages(conversationId, 0, PAGE_SIZE);

      // Fetch consumer credits in parallel with messages
      if (consumer?.id) {
        fetchConsumerCreditsById(consumer.id);
      }

      // Handle paginated API response
      if (result?.data) {
        const { messages: messagesData, totalCount, hasMore } = result.data;
        
        setTotalMessageCount(totalCount);
        setHasMoreMessages(hasMore);
        setCurrentSkip(PAGE_SIZE); // Next load will skip first PAGE_SIZE

        if (Array.isArray(messagesData)) {
          const formattedMessages = messagesData.map((apiMessage) => ({
            id: apiMessage.id,
            text: apiMessage.body || apiMessage.text,
            direction:
              apiMessage.direction === "Outbound" ? "outbound" : "inbound",
            timestamp: apiMessage.createdAt || apiMessage.CreatedAt || apiMessage.timestamp,
            sender: apiMessage.operatorName || consumer?.name || "Consumer",
            operatorId: apiMessage.operatorId,
            consumerId: apiMessage.consumerId,
          }));
          setMessages(formattedMessages);
        } else {
          setMessages([]);
        }
      } else {
        console.warn("⚠️ Unexpected API response format:", result);
        setMessages([]);
        setHasMoreMessages(false);
      }
    } catch (error) {
      console.error("❌ Failed to fetch messages:", error);
      showNotification("Failed to load messages", "error");
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  }, [
    conversationId,
    getPaginatedMessages,
    consumer?.name,
    consumer?.id,
    showNotification,
    lastRefreshTime,
    fetchConsumerCreditsById,
  ]);

  // Load more messages when scrolling up (pagination)
  const loadMoreMessages = useCallback(async () => {
    if (!conversationId || loadingMoreMessages || !hasMoreMessages) return;

    setLoadingMoreMessages(true);
    
    // Store scroll position to restore after loading
    const container = messagesContainerRef.current;
    const previousScrollHeight = container?.scrollHeight || 0;

    try {
      const result = await getPaginatedMessages(conversationId, currentSkip, PAGE_SIZE);

      if (result?.data) {
        const { messages: messagesData, hasMore } = result.data;
        
        setHasMoreMessages(hasMore);
        setCurrentSkip(prev => prev + PAGE_SIZE);

        if (Array.isArray(messagesData) && messagesData.length > 0) {
          const formattedMessages = messagesData.map((apiMessage) => ({
            id: apiMessage.id,
            text: apiMessage.body || apiMessage.text,
            direction:
              apiMessage.direction === "Outbound" ? "outbound" : "inbound",
            timestamp: apiMessage.createdAt || apiMessage.CreatedAt || apiMessage.timestamp,
            sender: apiMessage.operatorName || consumer?.name || "Consumer",
            operatorId: apiMessage.operatorId,
            consumerId: apiMessage.consumerId,
          }));

          // Prepend older messages to the beginning
          setMessages(prev => {
            // Filter out any duplicates
            const existingIds = new Set(prev.map(m => m.id));
            const newMessages = formattedMessages.filter(m => !existingIds.has(m.id));
            return [...newMessages, ...prev];
          });

          // Restore scroll position after new messages are prepended
          setTimeout(() => {
            if (container) {
              const newScrollHeight = container.scrollHeight;
              container.scrollTop = newScrollHeight - previousScrollHeight;
            }
          }, 50);
        }
      }
    } catch (error) {
      console.error("❌ Failed to load more messages:", error);
      showNotification("Failed to load more messages", "error");
    } finally {
      setLoadingMoreMessages(false);
    }
  }, [
    conversationId,
    currentSkip,
    hasMoreMessages,
    loadingMoreMessages,
    getPaginatedMessages,
    consumer?.name,
    showNotification,
  ]);

  // Handle scroll event to detect when user scrolls near top
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      // Load more when user scrolls within 100px of the top
      if (container.scrollTop < 100 && hasMoreMessages && !loadingMoreMessages) {
        loadMoreMessages();
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [hasMoreMessages, loadingMoreMessages, loadMoreMessages]);

  // Initialize messages - use provided allMessages or fetch from API
  const hasInitializedRef = useRef(false);
  
  // Reset pagination state when conversation changes
  useEffect(() => {
    hasInitializedRef.current = false;
    setMessages([]);
    setCurrentSkip(0);
    setHasMoreMessages(false);
    setTotalMessageCount(0);
  }, [conversationId]);

  useEffect(() => {
    if (hasInitializedRef.current) return;

    if (allMessages && Array.isArray(allMessages) && allMessages.length > 0) {
      // Use provided messages (e.g., from MessageReply component)
      const formattedMessages = allMessages.map((apiMessage) => ({
        id: apiMessage.id,
        text: apiMessage.body || apiMessage.text,
        direction: apiMessage.direction === "Outbound" ? "outbound" : "inbound",
        timestamp: apiMessage.createdAt || apiMessage.timestamp,
        sender: apiMessage.operatorName || consumer?.name || "Consumer",
        operatorId: apiMessage.operatorId,
        consumerId: apiMessage.consumerId,
      }));
      setMessages(formattedMessages);
      
      // Use pagination info from parent if available
      if (initialPaginationInfo) {
        setTotalMessageCount(initialPaginationInfo.totalCount || 0);
        setHasMoreMessages(initialPaginationInfo.hasMore || false);
        setCurrentSkip(allMessages.length); // Next load will skip these messages
      } else {
        // Fallback: assume there might be more if we got PAGE_SIZE messages
        setHasMoreMessages(allMessages.length >= PAGE_SIZE);
        setCurrentSkip(allMessages.length);
      }
      hasInitializedRef.current = true;
      
      // Scroll to bottom after initial messages load (instant, not smooth)
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
      }, 50);
    } else if (conversationId && !hasInitializedRef.current) {
      // Fetch messages independently for real-time scenarios
      fetchMessages().then(() => {
        hasInitializedRef.current = true;
        // Scroll to bottom after fetching messages (instant, not smooth)
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
        }, 50);
      });
    }

    // Set consumer credits from prop or fetch from API
    if (consumer?.creditBalance !== undefined) {
      setConsumerCredits(consumer.creditBalance);
    } else if (consumer?.id) {
      fetchConsumerCredits();
    }
  }, [
    allMessages,
    conversationId,
    consumer?.name,
    consumer?.creditBalance,
    consumer?.id,
    initialPaginationInfo,
    fetchMessages,
    fetchConsumerCredits,
  ]);

  // Track SignalR connection status
  useEffect(() => {
    if (signalR.isConnected !== isConnected) {
      setIsConnected(signalR.isConnected);

      if (signalR.isConnected) {
        showNotification("Connected to real-time messaging", "success");
        // Mark messages as delivered when connected
        if (conversationId) {
          markAllMessagesAsDelivered(conversationId);
        }
      } else {
        showNotification("Disconnected from real-time messaging", "warning");
      }
    }
  }, [
    signalR.isConnected,
    isConnected,
    showNotification,
    conversationId,
    markAllMessagesAsDelivered,
  ]);

  // Store handler refs for proper cleanup (prevents duplicate handlers)
  const handlersRef = useRef({
    creditBalanceUpdated: null,
    // REMOVED: receiveMessage - now handled via custom event from SignalRProvider only
    // REMOVED: newMessageNotification - now handled via custom event from SignalRProvider only
    conversationLocked: null,
    conversationUnlocked: null,
    messagesMarkedAsDelivered: null,
    error: null,
    customReceiveMessage: null,
    customNewMessageNotification: null,
  });

  // Setup SignalR event handlers - Use refs to store handlers for proper cleanup
  // CRITICAL: Only listen to custom events from SignalRProvider to prevent duplicate handling!
  // SignalRProvider is the SINGLE source of truth for SignalR events
  const eventHandlersSetupRef = useRef(false);
  useEffect(() => {
    // Skip if already setup or no connection
    if (!signalR.connection) return;
    
    // If handlers are already set up, clean them up first before re-registering
    if (eventHandlersSetupRef.current) {
      if (handlersRef.current.creditBalanceUpdated) {
        signalR.connection.off("CreditBalanceUpdated", handlersRef.current.creditBalanceUpdated);
      }
      // REMOVED: Direct SignalR handlers - now using custom events only
      if (handlersRef.current.conversationLocked) {
        signalR.connection.off("ConversationLocked", handlersRef.current.conversationLocked);
      }
      if (handlersRef.current.conversationUnlocked) {
        signalR.connection.off("ConversationUnlocked", handlersRef.current.conversationUnlocked);
      }
      if (handlersRef.current.messagesMarkedAsDelivered) {
        signalR.connection.off("MessagesMarkedAsDelivered", handlersRef.current.messagesMarkedAsDelivered);
      }
      if (handlersRef.current.error) {
        signalR.connection.off("Error", handlersRef.current.error);
      }
      // Clean up custom event listeners
      if (handlersRef.current.customReceiveMessage) {
        window.removeEventListener('signalr-receive-message', handlersRef.current.customReceiveMessage);
      }
      if (handlersRef.current.customNewMessageNotification) {
        window.removeEventListener('signalr-new-message-notification', handlersRef.current.customNewMessageNotification);
      }
    }

    // Create new handlers (capture current values)
    const consumerId = consumer?.id;
    const convId = conversationId;

    // Credit balance updates (keep direct handler - no duplicate in SignalRProvider)
    handlersRef.current.creditBalanceUpdated = (creditInfo) => {
      if (creditInfo.consumerId === consumerId) {
        setConsumerCredits(creditInfo.newBalance);
        showNotification(`Consumer credits updated: ${creditInfo.newBalance}`, "info");
      }
    };

    // FIXED: Listen to custom event from SignalRProvider ONLY (not direct SignalR)
    // This prevents duplicate handling since SignalRProvider already handles the raw event
    handlersRef.current.customReceiveMessage = (event) => {
      handleNewMessage(event.detail);
    };

    // REMOVED: Snackbar notification for new consumer messages - too intrusive
    // The message will appear in the chat interface which is sufficient

    // Conversation locking (keep direct - may not be in SignalRProvider yet)
    handlersRef.current.conversationLocked = (lockInfo) => {
      if (lockInfo.conversationId === convId) {
        showNotification(`Conversation locked by ${lockInfo.operatorName}`, "info");
      }
    };

    // Conversation unlocking
    handlersRef.current.conversationUnlocked = (unlockInfo) => {
      if (unlockInfo.conversationId === convId) {
        showNotification("Conversation unlocked", "info");
      }
    };

    // Messages marked as delivered
    handlersRef.current.messagesMarkedAsDelivered = (deliveryInfo) => {
      // Message delivery status tracked
    };

    // Connection errors
    handlersRef.current.error = (error) => {
      console.error("SignalR error:", error);
      showNotification("Real-time messaging error occurred", "error");
    };

    // Register ONLY handlers that don't have duplicates in SignalRProvider
    signalR.connection.on("CreditBalanceUpdated", handlersRef.current.creditBalanceUpdated);
    // REMOVED: signalR.connection.on("ReceiveMessage") - use custom event instead
    // REMOVED: signalR.connection.on("NewMessageNotification") - use custom event instead
    signalR.connection.on("ConversationLocked", handlersRef.current.conversationLocked);
    signalR.connection.on("ConversationUnlocked", handlersRef.current.conversationUnlocked);
    signalR.connection.on("MessagesMarkedAsDelivered", handlersRef.current.messagesMarkedAsDelivered);
    signalR.connection.on("Error", handlersRef.current.error);
    
    // Listen for custom events from SignalRProvider (SINGLE source of truth)
    window.addEventListener('signalr-receive-message', handlersRef.current.customReceiveMessage);
    // REMOVED: signalr-new-message-notification listener - snackbar was too intrusive

    eventHandlersSetupRef.current = true;

    // Cleanup function - remove specific handlers
    return () => {
      if (signalR.connection) {
        if (handlersRef.current.creditBalanceUpdated) {
          signalR.connection.off("CreditBalanceUpdated", handlersRef.current.creditBalanceUpdated);
        }
        // REMOVED: Direct ReceiveMessage/NewMessageNotification cleanup - using custom events
        if (handlersRef.current.conversationLocked) {
          signalR.connection.off("ConversationLocked", handlersRef.current.conversationLocked);
        }
        if (handlersRef.current.conversationUnlocked) {
          signalR.connection.off("ConversationUnlocked", handlersRef.current.conversationUnlocked);
        }
        if (handlersRef.current.messagesMarkedAsDelivered) {
          signalR.connection.off("MessagesMarkedAsDelivered", handlersRef.current.messagesMarkedAsDelivered);
        }
        if (handlersRef.current.error) {
          signalR.connection.off("Error", handlersRef.current.error);
        }
      }
      // Clean up custom event listeners
      if (handlersRef.current.customReceiveMessage) {
        window.removeEventListener('signalr-receive-message', handlersRef.current.customReceiveMessage);
      }
      // REMOVED: customNewMessageNotification cleanup - handler was removed
      eventHandlersSetupRef.current = false;
    };
  }, [signalR.connection, consumer?.id, conversationId, showNotification, handleNewMessage]);

  // Cleanup: Leave conversation when component unmounts
  useEffect(() => {
    return () => {
      if (conversationId && signalR.connection && signalR.isConnected) {
        signalR.connection.invoke("LeaveConversation", conversationId).catch(console.error);
      }
    };
  }, [conversationId, signalR]);

  // REMOVED: Duplicate JoinConversation effect - the first one (lines 97-134) already handles join/leave properly

  // Handle SignalR reconnection - refresh messages to catch any missed during disconnect
  useEffect(() => {
    const handleReconnection = async (event) => {
      if (conversationId) {
        // Wait a moment for connection to stabilize
        setTimeout(() => {
          fetchMessages();
          showNotification('Reconnected to real-time messaging', 'success');
        }, 500);
      }
    };

    window.addEventListener('signalr-reconnected', handleReconnection);
    
    return () => {
      window.removeEventListener('signalr-reconnected', handleReconnection);
    };
  }, [conversationId, fetchMessages, showNotification]);

  // REMOVED duplicate fetch effect - initialization effect already handles fetching
  // This was causing double API calls and scroll jumping

  // Auto-scroll to bottom only for NEW messages (not when loading older messages)
  // Track the last message ID to detect if new messages were added at the END
  const lastMessageIdRef = useRef(null);
  const isInitialLoadDoneRef = useRef(false);
  useEffect(() => {
    if (messages.length === 0) {
      lastMessageIdRef.current = null;
      return;
    }
    
    const currentLastMessageId = messages[messages.length - 1]?.id;
    
    // Skip during initial load - handled by initialization effect
    if (!isInitialLoadDoneRef.current) {
      // Mark initial load as done after first messages are set
      setTimeout(() => {
        isInitialLoadDoneRef.current = true;
        lastMessageIdRef.current = currentLastMessageId;
      }, 200);
      return;
    }
    
    // Only scroll if the LAST message changed (new message at end)
    // Don't scroll if older messages were prepended (last message ID stays same)
    if (lastMessageIdRef.current !== currentLastMessageId && lastMessageIdRef.current !== null) {
      scrollToBottom();
    }
    
    lastMessageIdRef.current = currentLastMessageId;
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSendReply = async () => {
    if (!conversationId) {
      showNotification("No conversation selected", "error");
      return;
    }

    if (!replyText.trim()) {
      showNotification("Please enter a message", "warning");
      return;
    }

    // Operator message validation (80-160 characters)
    if (replyText.length < 80) {
      showNotification(
        "Operator message must be at least 80 characters long",
        "error"
      );
      return;
    }

    if (replyText.length > 160) {
      showNotification(
        "Operator message must be 160 characters or less",
        "error"
      );
      return;
    }

    // Credit validation for operator messages
    if (consumerCredits <= 0) {
      showNotification(
        "Consumer has no credits remaining. Reply cannot be sent.",
        "error"
      );
      return;
    }

    // Remove connection requirement - allow API fallback
    setSending(true);

    try {
      // Admin portal - always send operator message
      const operatorMessageData = {
        ConversationId: conversationId,
        Body: replyText.trim(),
        OperatorId: userId,
      };

      const result = await sendOperatorMessage(operatorMessageData);

      if (result && !result.error) {
        // Fetch updated credit balance after sending a message
        if (consumer?.id) {
          await fetchConsumerCreditsById(consumer.id);
        }
        showNotification(
          "Reply sent successfully! Consumer credit deducted.",
          "success"
        );
        
        // Call the callback to notify parent (stops timer)
        if (onOperatorMessageSent) {
          onOperatorMessageSent();
        }
      } else {
        throw new Error(result?.error || "Failed to send operator message");
      }

      // Clear input and reply state
      setReplyText("");
      setReplyingTo(null);
    } catch (error) {
      console.error("Failed to send message:", error);
      showNotification("Failed to send message. Please try again.", "error");
    } finally {
      setSending(false);
    }
  };

  const handleReplyToMessage = (messageId, messageText) => {
    // Set the message being replied to (WhatsApp style)
    setReplyingTo({
      id: messageId,
      text: messageText,
      sender: messages.find((m) => m.id === messageId)?.sender || "Consumer",
    });

    // Clear any existing reply text
    setReplyText("");

    // Scroll to reply input
    document
      .querySelector("[data-reply-input]")
      ?.scrollIntoView({ behavior: "smooth" });

    showNotification("Replying to message. Type your response below.", "info");
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
    setReplyText("");
  };

  const storedUser = JSON.parse(localStorage.getItem("user"));

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Connection Status - Only show status, no manual reconnection */}
      <Box
        sx={{
          p: 1,
          bgcolor: isConnected ? "#e8f5e8" : "#ffeaa7",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isConnected ? (
            <>
              <Wifi sx={{ color: "#4caf50", fontSize: 16 }} />
              <Typography variant="caption" sx={{ color: "#4caf50" }}>
                Connected to real-time messaging
              </Typography>
            </>
          ) : (
            <>
              <WifiOff sx={{ color: "#ff9800", fontSize: 16 }} />
              <Typography variant="caption" sx={{ color: "#ff9800" }}>
                Connecting to real-time messaging...
              </Typography>
            </>
          )}
          <Chip
            label={storedUser?.role || "Operator"}
            size="small"
            sx={{ ml: "auto", fontSize: "0.7rem" }}
            color="primary"
          />
        </Box>
      </Box>

      {/* Messages with Date Stripes */}
      <Box
        ref={messagesContainerRef}
        sx={{
          flex: 1,
          overflow: "auto",
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {/* Loading more messages indicator at top */}
        {loadingMoreMessages && (
          <Box sx={{ textAlign: "center", py: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              Loading older messages...
            </Typography>
          </Box>
        )}
        
        {/* Show "Load more" indicator when there are more messages */}
        {hasMoreMessages && !loadingMoreMessages && messages.length > 0 && (
          <Box sx={{ textAlign: "center", py: 1 }}>
            <Typography variant="caption" color="text.secondary">
              ↑ Scroll up to load more messages
            </Typography>
          </Box>
        )}

        {loadingMessages ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Loading messages...
            </Typography>
          </Box>
        ) : messages.length > 0 ? (
          groupMessagesWithDateStripes(messages).map((item, idx) => {
            if (item.type === "date-stripe") {
              // Render date separator
              const today = new Date();
              const isToday = item.date.toDateString() === today.toDateString();
              const isYesterday =
                item.date.toDateString() ===
                new Date(today.getTime() - 86400000).toDateString();
              let label = formatLocalDate(item.date);
              if (isToday) label = "Today";
              else if (isYesterday) label = "Yesterday";
              return (
                <Box
                  key={`date-stripe-${formatLocalDate(item.date)}`}
                  sx={{ textAlign: "center", my: 2 }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      background: "#e0e0e0",
                      px: 2,
                      py: 0.5,
                      borderRadius: 2,
                      fontWeight: 600,
                      color: "#555",
                    }}
                  >
                    {label}
                  </Typography>
                </Box>
              );
            } else if (item.type === "message") {
              const isMyMsg = isMyMessage(item);
              return (
                <Box
                  key={item.id}
                  sx={{
                    display: "flex",
                    justifyContent: isMyMsg ? "flex-end" : "flex-start",
                    mb: 1,
                  }}
                >
                  <Paper
                    sx={{
                      padding: "8px 20px",
                      width: "fit-content",
                      maxWidth: "500px",
                      borderRadius: "8px",
                      position: "relative",
                      color: isMyMsg ? "#fff" : "#000",
                      border: isMyMsg
                        ? "1px solid var(--color-primary)"
                        : "1px solid #e5e7eb",
                      bgcolor: isMyMsg ? "var(--color-primary)" : "#f8f8f8",
                      boxShadow: "none",
                    }}
                  >
                    {/* Reply Reference (WhatsApp style) */}
                    {item.replyTo && (
                      <Box
                        sx={{
                          borderLeft: "3px solid #d32f2f",
                          pl: 1,
                          mb: 1,
                          bgcolor: "rgba(0,0,0,0.05)",
                          borderRadius: "4px",
                          p: 1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="primary"
                          fontWeight="bold"
                        >
                          {item.replyTo.sender}
                        </Typography>
                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                        >
                          {item.replyTo.text.length > 50
                            ? `${item.replyTo.text.substring(0, 50)}...`
                            : item.replyTo.text}
                        </Typography>
                      </Box>
                    )}

                    <Typography
                      variant="body2"
                      sx={{ mb: 1, wordBreak: "break-word", whiteSpace: "pre-wrap" }}
                      fontSize={13}
                    >
                      {item.text}
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        width={"100%"}
                        color={!isMyMsg ? "text.secondary" : "#fff"}
                        textAlign={isMyMsg ? "right" : "left"}
                      >
                        {formatLocalTime(item.timestamp)}
                      </Typography>
                    </Box>
                  </Paper>
                </Box>
              );
            } else {
              return null;
            }
          })
        ) : (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign={"center"}
            >
              No messages in this conversation yet
            </Typography>
          </Box>
        )}
        {/* Credit Warning - Admin Portal */}
        {consumerCredits <= 0 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
            }}
          >
            <h4 style={{ margin: 0, color: "var(--color-error)" }}>
              0 Credits Remaining
            </h4>
            <p style={{ textAlign: "center", fontSize: 13, color: "#555" }}>
              This consumer has no remaining credits. <br />
              They cannot receive any replies until they add more.
            </p>
          </div>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Reply Input */}
      <Box
        sx={{
          p: 2,
          borderTop: "1px solid #e0e0e0",
          bgcolor: "#fafafa",
        }}
      >
        {/* WhatsApp-style Reply Preview */}
        {replyingTo && (
          <Box
            sx={{
              bgcolor: "#fff",
              border: "1px solid #e0e0e0",
              borderRadius: "8px 8px 0 0",
              p: 2,
              mb: 0,
              borderLeft: "3px solid #d32f2f",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="primary" fontWeight="bold">
                  Replying to {replyingTo.sender}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {replyingTo.text.length > 60
                    ? `${replyingTo.text.substring(0, 60)}...`
                    : replyingTo.text}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={handleCancelReply}
                sx={{ ml: 1, color: "text.secondary" }}
              >
                ✕
              </IconButton>
            </Box>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "flex-start",
            position: "relative",
          }}
        >
          {/* Text Input */}
          <textarea
            maxLength={160}
            style={{
              flex: 1,
              resize: "none",
              padding: "10px 30px 10px 10px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              fontSize: "16px",
              fontFamily: "Roboto, sans-serif",
            }}
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            disabled={
              !conversationId || // Need a conversation to send messages
              sending ||
              consumerCredits <= 0 // Operator needs consumer credits
            }
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSendReply();
              }
            }}
          />
          <p
            style={{
              position: "absolute",
              right: "70px",
              bottom: "-4px",
              fontSize: "12px",
              color: "#888",
            }}
          >
            {replyText.length}/{160}
          </p>
          <div
            style={{
              display: "flex",
              alignItems: "end",
              width: 40,
              height: 40,
              marginBottom: 4,
              cursor:
                sending ||
                !replyText.trim() ||
                consumerCredits <= 0 ||
                !conversationId ||
                replyText.length < 80 ||
                replyText.length > 160
                  ? "not-allowed"
                  : "pointer",
              opacity:
                sending || !replyText.trim() || consumerCredits <= 0 ? 0.5 : 1,
              borderRadius: "4px",
              transition: "opacity 0.2s",
            }}
            onClick={handleSendReply}
          >
            <Send
              style={{
                transform: "rotate(320deg)",
                color:
                  sending ||
                  !replyText.trim() ||
                  consumerCredits <= 0 ||
                  !conversationId ||
                  replyText.length < 80 ||
                  replyText.length > 160
                    ? "#ccc"
                    : "#007bff",
              }}
            />
          </div>
          {/* Send Button */}
          {/* <Button
            variant="contained"
            startIcon={<Send />}
            disabled={!replyText.trim() || sending || consumerCredits <= 0}
            sx={{
              backgroundColor: "var(--color-primary)",
              "&:hover": { backgroundColor: "var(--color-secondary)" },
              height: "fit-content",
              minWidth: "120px",
              mt: 1,
            }}
          >
            {sending ? "Sending..." : "Send Reply"}
          </Button> */}
        </Box>

        {/* Credit Info - Admin Portal */}
        {/* <Box
          sx={{
            mt: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color:
                consumerCredits > 5
                  ? "text.secondary"
                  : consumerCredits > 0
                  ? "orange"
                  : "red",
              fontWeight: "bold",
            }}
          >
            Consumer Credits: {consumerCredits}
          </Typography>
        </Box> */}
      </Box>
    </Box>
  );
};

export default ChatInterface;
